import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';

const programApi = createApi({
  reducerPath: 'programs',
  tagTypes: ['Program'],
  baseQuery,
  endpoints(builder) {
    return {
      fetchPrograms: builder.query({
        providesTags: (result) =>
          result
            ? [
                { type: 'Program', id: 'LIST' },
                ...result.map((r) => ({ type: 'Program', id: r.id })),
              ]
            : [{ type: 'Program', id: 'LIST' }],
        query: (coach) => ({
          url: `?resource=program&coach=eq.${coach}&order=id.desc`,
          method: 'GET',
        }),
      }),

      createProgram: builder.mutation({
        invalidatesTags: [{ type: 'Program', id: 'LIST' }],
        query: (data) => ({
          url: `?resource=program`,
          method: 'POST',
          body: data,
          headers: {
            Prefer: 'return=representation',
            'Content-Type': 'application/json',
          },
        }),
      }),

      updateProgram: builder.mutation({
        invalidatesTags: (result, error, { id: programId }) => [
          { type: 'Program', id: programId },
          { type: 'Program', id: 'LIST' },
        ],
        query: ({ id: programId, submitData }) => ({
          url: `?resource=program&id=eq.${programId}`,
          method: 'PATCH',
          body: submitData,
          headers: {
            Prefer: 'return=representation',
            'Content-Type': 'application/json',
          },
        }),
      }),

      deleteProgram: builder.mutation({
        invalidatesTags: (result, error, programId) => [
          { type: 'Program', id: programId },
          { type: 'Program', id: 'LIST' },
        ],
        query: (programId) => ({
          url: `?resource=program&id=eq.${programId}`,
          method: 'DELETE',
        }),
      }),

      fetchProgram: builder.query({
        providesTags: (result, error, arg) => [{ type: 'Program', id: arg }],
        query: (programId) => ({
          url: `?resource=program&id=eq.${programId}&select=*,workout(*)`,
          method: 'GET',
        }),
      }),

      // Duplica un programa completo en UNA sola llamada (RPC server-side), en
      // lugar de la ráfaga de ~40 peticiones del antiguo program.service. Evita
      // los 401 por rate-limit de /userinfo y es atómico. La propiedad del
      // programa origen la verifica el proxy.
      duplicateProgram: builder.mutation({
        invalidatesTags: [{ type: 'Program', id: 'LIST' }],
        query: ({ programId, newName }) => ({
          url: `?resource=rpc/duplicate_program`,
          method: 'POST',
          body: { p_program_id: programId, new_name: newName },
        }),
      }),

      fetchAllProgramDetailsByAthlete: builder.query({
        providesTags: (result) =>
          result
            ? [
                { type: 'Program', id: 'LIST' },
                ...result.map((r) => ({ type: 'Program', id: r.program })),
              ]
            : [{ type: 'Program', id: 'LIST' }],
        query: ({ athleteId, programId }) => ({
          url: `?resource=program_athlete&athlete=eq.${athleteId}&program=eq.${programId}&select=*,program_details:program(*,workout(*,workout_exercise(*,exercise_details:exercise(*),set_objective(*,set_executed(*)))))`,
          method: 'GET',
        }),
      }),
    };
  },
});

export const {
  useFetchProgramsQuery,
  useCreateProgramMutation,
  useUpdateProgramMutation,
  useDeleteProgramMutation,
  useFetchProgramQuery,
  useLazyFetchProgramQuery,
  useFetchAllProgramDetailsByAthleteQuery,
  useDuplicateProgramMutation,
} = programApi;

export { programApi };
